import React, { useState, useRef, useMemo, useEffect, useLayoutEffect } from "react";
import { Marker, Popup } from "react-leaflet";
import { MarkerData } from "../..";
import L from "leaflet";
import { Coordinates } from "./MarkerCoordinates";
import { useAppSelector } from "../../../../../app/hooks";
import { selectUser } from "../../../../../features/user/userSlice";
import { useGetNotificationByIDQuery } from "../../../../../services/groupNotificationAPI";
import { UserPermissions } from "../../../../../features/groups/groupSlice";
import { GroupImage } from "./GroupImageSelect";
import { mapObjectToArray } from "../../../../../utils/GroupNotificationUtils";
import { useGetGroupImagesQuery } from "../../../../../services/groupAPI";
import { selectDashboard } from "../../../../../features/dashboards/dashboardSlice";
import {
  defaultActiveImage,
  defaultInactiveImage,
  defaultActiveAcknowledgedImage,
  getMarkerSize,
} from "./utils";
import Prompt from "../../../../../utils/Prompt";
import { useAcknowledgeMutation } from "../../../../../services/groupNotificationAPI";
import useGetLanguage from "../../../../../utils/useGetLanguage";
import { LanguageText } from "../../../../../dictionary/ChartsText";
import { Size } from "./MarkerSize";

export type MarkerSize = [number, number];
type NotificationMarkerProps = {
  dashboardID: string;
  marker: MarkerData;
  index: number;
  updateMarker: (updatedMarker: MarkerData, index: number) => void;
  removeMarker: (notificationID: string) => void;
  isDraggable: boolean;
};

const NotificationMarker = ({
  dashboardID,
  marker,
  index,
  updateMarker,
  removeMarker,
  isDraggable,
}: NotificationMarkerProps): JSX.Element => {
  const language: LanguageText = useGetLanguage({
    fileName: "ChartsText",
  });
  const [showAcknowledgePrompt, setShowAcknowledgePrompt] = useState<boolean>(false);
  const [isAcknowledge, setIsAcknowledge] = useState<boolean | null>(null);

  const [
    acknowledge,
    {
      isError: isAcknowledgeError,
      error: acknowledgeError,
      data: acknowledgeData,
      isSuccess: isAcknowledgeSuccess,
    },
  ] = useAcknowledgeMutation();

  const dashboard = useAppSelector((state) => selectDashboard(state, dashboardID));
  const groupID: string = dashboard?.group_id ?? "";
  const markerRef = useRef<L.Marker | null>(null);
  const notificationID: string = marker?.notificationID || "";

  const [location, setLocation] = useState<Coordinates>({
    lat: marker.lat || 0,
    lng: marker.lng || 0,
  });

  const [groupImages, setGroupImages] = useState<GroupImage[]>([]);
  const [markerSize, setMarkerSize] = useState<MarkerSize>(
    getMarkerSize(marker?.markerSize as Size, window.innerWidth)
  );

  const {
    data: groupNotificationData,
    isLoading,
    isFetching,
    error,
  } = useGetNotificationByIDQuery(notificationID, {
    skip: !notificationID,
    pollingInterval: 1000 * 10,
  });

  let image: GroupImage;

  if (groupNotificationData?.configuration?.latch_on) {
    if (
      new Date(groupNotificationData?.status?.acknowledged_at) <
      new Date(groupNotificationData?.status?.triggered_at)
    ) {
      image =
        groupImages?.find((image) => image.image_id === marker.activeStatusImageId) ||
        defaultInactiveImage;
    } else {
      if (groupNotificationData?.status?.current) {
        image =
          groupImages?.find(
            (image) => image.image_id === marker.activeAcknowledgedImageId
          ) || defaultActiveAcknowledgedImage;
      } else {
        image =
          groupImages?.find((image) => image.image_id === marker.inactiveStatusImageId) ||
          defaultActiveImage;
      }
    }
  } else {
    if (groupNotificationData?.status?.current) {
      image =
        groupImages?.find((image) => image.image_id === marker.activeStatusImageId) ||
        defaultActiveImage;
    } else {
      image =
        groupImages?.find((image) => image.image_id === marker.inactiveStatusImageId) ||
        defaultInactiveImage;
    }
  }

  const { data: groupImageData } = useGetGroupImagesQuery(
    { group_id: groupID ?? "" },
    { skip: !groupID }
  );

  const customIcon = useMemo(
    () =>
      L.icon({
        iconUrl: image?.url,
        iconSize: markerSize,
        iconAnchor: [markerSize[0] / 2, markerSize[1]], // (bottom center)
        popupAnchor: [0, -markerSize[1] * 0.6],
      }),
    [image?.url, markerSize]
  );

  const handleDragend = (newPosition: Coordinates) => {
    setLocation(newPosition);
    updateMarker({ ...marker, ...newPosition }, index);
  };

  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker) {
          handleDragend(marker.getLatLng());
        }
      },
    }),
    [index, marker, updateMarker]
  );

  const handleCloseAcknowledgePrompt = (isAcknowledge: boolean) => {
    setIsAcknowledge(isAcknowledge);
    setShowAcknowledgePrompt(false);
  };

  const handleAcknowledgeBtnClick = () => {
    setShowAcknowledgePrompt(true);
  };

  useEffect(() => {
    const marker = markerRef.current;
    if (!marker) return;
    const timeout = setTimeout(() => {
      const popup = marker.getPopup();
      if (groupNotificationData?.status?.current && popup) {
        popup.options.autoPan = false;
        marker.openPopup();
      } else {
        marker.closePopup();
      }
    }, index * 50);
    return () => clearTimeout(timeout);
  }, [groupNotificationData?.status?.current]);

  useEffect(() => {
    if (groupImageData) {
      const imagesArray = mapObjectToArray(groupImageData);
      setGroupImages(imagesArray);
    }
  }, [groupImageData]);

  useEffect(() => {
    if (marker) {
      const { lat, lng, markerSize } = marker;
      setLocation({
        lat: lat || 0,
        lng: lng || 0,
      });
      setMarkerSize(getMarkerSize(markerSize as Size, window.innerWidth));
    }
  }, [marker]);

  useEffect(() => {
    if (isAcknowledge) {
      acknowledge({ notificationId: groupNotificationData?.id, language: null });
      setIsAcknowledge(false);
    }
  }, [isAcknowledge]);

  // This is to auto-remove any marker that has a notification deleted or invalidated
  useEffect(() => {
    if (error) {
      removeMarker(marker?.notificationID ?? "");
    }
  }, [error, index, removeMarker]);

  useEffect(() => {
    function handleResize() {
      setMarkerSize(getMarkerSize(marker?.markerSize as Size, window.innerWidth));
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!groupNotificationData) return <></>;
  return (
    <>
      {showAcknowledgePrompt && (
        <Prompt
          onClose={handleCloseAcknowledgePrompt}
          title="Are you Sure?"
          confirmText="Aknowledge"
          confirmBtnColor="blue"
          cancelText="Cancel"
        />
      )}
      <Marker
        draggable={isDraggable}
        eventHandlers={eventHandlers}
        position={location}
        ref={markerRef}
        icon={customIcon}
      >
        <Popup autoClose={false} closeOnClick={false} closeButton={true} autoPan={false}>
          <div className="p-2 bg-white rounded-lg w-38 max-w-full">
            <div className="flex items-center mb-2">
              <h3 className="text-lg font-semibold text-gray-800">
                {groupNotificationData?.name}
              </h3>
              <span
                className={`ml-2 h-3 w-3 rounded-full ${
                  groupNotificationData?.status?.current ? "bg-green-500" : "bg-red-500"
                }`}
              ></span>
            </div>

            <div className="flex justify-end">
              {groupNotificationData?.configuration?.latch_on &&
                groupNotificationData?.status?.triggered && (
                  <button
                    className="px-3 py-1 text-xs sm:text-sm bg-blue-500 text-white font-medium rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    onClick={handleAcknowledgeBtnClick}
                  >
                    {language?.charts?.map?.markers?.notification?.acknowledge}
                  </button>
                )}
            </div>
          </div>
        </Popup>
      </Marker>
    </>
  );
};

export default React.memo(NotificationMarker);
